import * as moment from 'moment';
import { EXR_SEARCH_REG, IMG_EXTENSIONS } from '../constants/file.constants';

/**
 * Класс файла
 *
 * @package Fields
 * @version 2.0
 * @author Bolotov I.P.
 * @since 10.03.2023
 * @copyright (c) Internet-Frigate {@link http://www.ifrigate.ru}
 */
export class File {
    constructor(data: any) {
        this.id = Number(data.id ?? 0);
        this._filePath = data?.file_name ?? '';
        this._name = data?.name ?? '';
        this.size = Number(data?.size ?? data?.file_size ?? 0);
        this._uuidURL = data?.file_name_ext ?? '';
        this.uuid = data?.ext_uuid ?? '';

        let dateValue: any = data.download_date;

        // Если это объект с полем date
        if (typeof dateValue === 'object' && dateValue !== null && dateValue.date) {
            dateValue = dateValue.date;
        }

        // Если это строка - парсим напрямую
        if (typeof dateValue === 'string') {
            // Заменяем пробел на T для корректного парсинга
            const formattedDate = dateValue.replace(' ', 'T');
            this.uploadDate = moment(formattedDate).format('YYYY-MM-DD HH:mm:ss');
        } else {
            // Если ничего не подошло - Invalid date
            this.uploadDate = 'Invalid date';
        }

        if (data?.caption && typeof data?.caption === 'object') {
            this._caption = data?.caption;
        }

        this.setExt();
        this.checkIsImg();
    }

    /** ID файла */
    public readonly id: number = null;

    public readonly uuid: string = null;

    /** Размер файла */
    public readonly size: number = 0;

    /** UUID-URL для внешнего доступа к файлу */
    protected readonly _uuidURL: string = '';

    /** Имя файла */
    protected _name: string = '';

    /** Путь до файла */
    protected readonly _filePath: string = '';

    /** Дата загрузки файла */
    public readonly uploadDate: string = null;

    /** Флаг - файл является графическим */
    protected _isImg: boolean = false;

    /** Расширение файла */
    protected _ext: string;

    /** Caption */
    protected _caption: { [key: string]: string } = null;

    /** Получить флаг - графический файл */
    public get isImg(): boolean {
        return this._isImg;
    }

    /** Настоящее имя файла */
    public get fileName(): string {
        return this._name;
    }

    public set fileName(name: string) {
        this._name = name;
    }

    /** Расширение файла */
    public get ext(): string {
        return this._ext;
    }

    /** Путь для получения данных файла */
    public getFilePatch(full: boolean = false): string {
        if (full) {
            return this._filePath;
        }
        return this._uuidURL;
    }

    /** Установка расширения файла */
    protected setExt(): void {
        let name: string = '';
        if (this._filePath?.length > 0) {
            name = this._filePath;
        } else if (this._name?.length > 0) {
            name = this._name;
        }

        if (name.length === 0) {
            throw new Error('Invalid file extension.');
        }

        const matchArr = name.match(EXR_SEARCH_REG);

        if (matchArr.length === 0) {
            throw new Error('Invalid file extension.');
        }

        this._ext = matchArr[0].toLowerCase();
    }

    /** Проверка, является ли файл графическим */
    protected checkIsImg(): void {
        this._isImg = IMG_EXTENSIONS.indexOf(this._ext) > 0;
    }

    /** Получить caption */
    public get caption(): { [key: string]: string } | null {
        return Object.assign({}, this._caption);
    }

    /** Сохранить caption */
    public set caption(caption: { [key: string]: string } | null) {
        if (Object.keys(caption).length === 0) {
            this._caption = null;
            return;
        }
        this._caption = caption;
    }
}
